import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { Box, Typography, IconButton, CircularProgress, Divider, Button } from "@mui/material";
import { IconChevronLeft, IconScan } from "@tabler/icons-react";
import { ozonAssembliesApi } from "../api";
import {
  bannerSx,
  bannerTextSx,
  barIconSx,
  cardSx,
  ds,
  dsText,
  sectionLabelSx,
  shellSx,
  topBarSx,
} from "../theme/keeperTheme";

/**
 * Очередь сборки Ozon: что нужно собрать, по кабинетам.
 * Данные — POST /v1/assembly/fbs/product|posting/list через /api/ozon-assemblies/on-assembly.
 * «Начать» ведёт на экран сканирования (/ozon/assemblies/start) по выбранному кабинету.
 */
export default function OzonAssembliesPage() {
  const { registerToolbarRefresh } = useOutletContext() || {};
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [byCabinet, setByCabinet] = useState([]);
  const [postingsCount, setPostingsCount] = useState(0);
  const [cabinetErrors, setCabinetErrors] = useState([]);
  const [selectedCabinetId, setSelectedCabinetId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await ozonAssembliesApi.onAssembly();
      const cabinets = Array.isArray(data.postings_by_cabinet) ? data.postings_by_cabinet : [];
      setItems(Array.isArray(data.items) ? data.items : []);
      setByCabinet(cabinets);
      setPostingsCount(typeof data.postings_count === "number" ? data.postings_count : 0);
      setCabinetErrors(Array.isArray(data.errors) ? data.errors : []);
      setSelectedCabinetId((prev) => {
        if (cabinets.length === 0) return null;
        const exists = cabinets.some((c) => String(c.connection_id) === String(prev));
        if (prev != null && exists) return prev;
        return cabinets.length === 1 ? cabinets[0].connection_id : null;
      });
    } catch (e) {
      setError(e?.message || "Ошибка загрузки");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!registerToolbarRefresh) return undefined;
    return registerToolbarRefresh(() => loadData());
  }, [registerToolbarRefresh, loadData]);

  const selectedItems =
    selectedCabinetId == null
      ? []
      : items
          .map((row) => {
            const list = Array.isArray(row.by_cabinet) ? row.by_cabinet : [];
            const hit = list.find((c) => String(c.connection_id) === String(selectedCabinetId));
            const qty = Number(hit?.qty) || 0;
            if (qty <= 0) return null;
            return { ...row, selectedCabinetQty: qty };
          })
          .filter(Boolean);

  return (
    <Box sx={shellSx}>
      <Box
        sx={{
          ...topBarSx,
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          px: 1.5,
          py: 1.25,
        }}
      >
        <IconButton onClick={() => navigate("/pick")} aria-label="Назад к выбору" sx={barIconSx}>
          <IconChevronLeft size={26} stroke={1.5} />
        </IconButton>
        <Typography sx={{ color: ds.text, fontWeight: 700, flex: 1 }}>
          Ozon — на сборке
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            if (selectedCabinetId == null) return;
            const cabinet = byCabinet.find(
              (c) => String(c.connection_id) === String(selectedCabinetId),
            );
            const q = new URLSearchParams({
              cabinet: String(selectedCabinetId),
              cabinet_name:
                (cabinet?.name && String(cabinet.name).trim()) || `Кабинет ${selectedCabinetId}`,
            });
            navigate(`/ozon/assemblies/start?${q.toString()}`);
          }}
          disabled={selectedCabinetId == null}
          startIcon={<IconScan size={20} stroke={1.75} />}
          sx={{
            flexShrink: 0,
            mr: 1.25,
            px: 2,
            py: 0.85,
            borderRadius: "999px",
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.875rem",
            letterSpacing: "0.02em",
            boxShadow: ds.shadowXs,
            bgcolor: ds.primary,
            "&:hover": { bgcolor: ds.primaryHover, boxShadow: ds.shadowMd },
          }}
        >
          Начать
        </Button>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", px: 2, py: 1.5 }}>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={36} sx={{ color: ds.primary }} />
          </Box>
        )}
        {!loading && error && (
          <Typography sx={{ color: dsText.danger, textAlign: "center", py: 4 }}>{error}</Typography>
        )}
        {!loading && !error && cabinetErrors.length > 0 && (
          <Box sx={{ ...bannerSx("warning"), mb: 1.5 }}>
            <Typography sx={{ ...bannerTextSx, fontWeight: 500 }}>
              {cabinetErrors.length === 1
                ? "Один кабинет Ozon не ответил:"
                : `${cabinetErrors.length} кабинета Ozon не ответили:`}{" "}
              {cabinetErrors.map((e) => e.error).join("; ")}
            </Typography>
          </Box>
        )}
        {!loading && !error && (
          <Box sx={{ ...bannerSx("info"), mb: 1.5 }}>
            <Typography sx={{ ...bannerTextSx, fontWeight: 500 }}>
              На Ozon коды маркировки передаются до сборки отправления, а этикетка появляется
              только после неё. Выберите кабинет и нажмите «Начать».
            </Typography>
          </Box>
        )}
        {!loading && !error && byCabinet.length > 0 && (
          <Box sx={{ ...cardSx, mb: 1.5, py: 1.4, px: 1.6 }}>
            <Typography
              sx={{ color: ds.text, fontWeight: 700, fontSize: "0.9375rem", lineHeight: 1.35 }}
            >
              Отправления по кабинетам
            </Typography>
            <Divider sx={{ borderColor: ds.border, my: 1.25 }} />
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.6 }}>
              {byCabinet.map((c) => {
                const label =
                  (c.name && String(c.name).trim()) ||
                  (c.connection_id != null ? `Кабинет ${c.connection_id}` : "Кабинет");
                const active = String(selectedCabinetId) === String(c.connection_id);
                return (
                  <Box
                    key={String(c.connection_id ?? label)}
                    onClick={() => setSelectedCabinetId(c.connection_id ?? null)}
                    sx={{
                      display: "flex",
                      alignItems: "baseline",
                      justifyContent: "space-between",
                      gap: 1.25,
                      minWidth: 0,
                      borderRadius: `${ds.radius}px`,
                      minHeight: 44,
                      px: 1.25,
                      py: 0.9,
                      cursor: "pointer",
                      bgcolor: active ? ds.primarySoft : ds.surface2,
                      border: `1px solid ${active ? ds.primary : ds.border}`,
                    }}
                  >
                    <Typography
                      sx={{
                        color: active ? ds.text : ds.text2,
                        fontSize: "0.78rem",
                        lineHeight: 1.4,
                        fontWeight: 600,
                        minWidth: 0,
                        flex: 1,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                      title={label}
                    >
                      {label}
                    </Typography>
                    <Typography
                      sx={{
                        color: active ? ds.primaryHover : ds.text,
                        fontSize: "0.8rem",
                        fontWeight: 700,
                        fontFeatureSettings: '"tnum"',
                        flexShrink: 0,
                      }}
                      title="Отправлений к сборке по этому кабинету"
                    >
                      {typeof c.count === "number" ? c.count : "—"}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
            <Typography sx={{ ...sectionLabelSx, mt: 1.25, fontWeight: 600 }}>
              Всего отправлений: {postingsCount}
            </Typography>
          </Box>
        )}
        {!loading && !error && byCabinet.length === 0 && (
          <Typography sx={{ color: ds.text2, textAlign: "center", py: 6 }}>
            Нет отправлений к сборке
          </Typography>
        )}
        {!loading && !error && byCabinet.length > 0 && selectedCabinetId == null && (
          <Typography sx={{ color: ds.text2, textAlign: "center", py: 2 }}>
            Выберите кабинет Ozon.
          </Typography>
        )}
        {!loading && !error && selectedCabinetId != null && selectedItems.length === 0 && (
          <Typography sx={{ color: ds.text2, textAlign: "center", py: 4 }}>
            Нет товаров к сборке в выбранном кабинете
          </Typography>
        )}
        {!loading &&
          !error &&
          selectedItems.map((row) => (
            <Box
              key={String(row.sku ?? row.offer_id)}
              sx={{ ...cardSx, mb: 1, py: 1.2, px: 1.4, display: "flex", gap: 1.25 }}
            >
              {row.picture_url ? (
                <Box
                  component="img"
                  src={row.picture_url}
                  alt=""
                  loading="lazy"
                  sx={{
                    width: 52,
                    height: 52,
                    flexShrink: 0,
                    borderRadius: `${ds.radius}px`,
                    objectFit: "cover",
                    bgcolor: ds.surface2,
                    border: `1px solid ${ds.border}`,
                  }}
                />
              ) : null}
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  sx={{
                    color: ds.text,
                    fontWeight: 700,
                    fontSize: "0.875rem",
                    lineHeight: 1.3,
                    mb: 0.35,
                  }}
                >
                  {row.title || `SKU ${row.sku}`}
                </Typography>
                <Typography
                  sx={{
                    color: ds.text2,
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    mb: 0.35,
                    lineHeight: 1.35,
                  }}
                >
                  {row.offer_id ? `Артикул ${row.offer_id}` : null}
                  {row.offer_id && row.sku ? " · " : null}
                  {row.sku ? `SKU ${row.sku}` : null}
                </Typography>
                <Box
                  sx={{
                    alignSelf: "flex-start",
                    display: "inline-block",
                    px: 1.1,
                    py: 0.4,
                    borderRadius: 999,
                    bgcolor: ds.primarySoft,
                    border: `1px solid ${ds.primarySoft2}`,
                  }}
                >
                  <Typography
                    sx={{
                      color: ds.primaryHover,
                      fontSize: "0.8rem",
                      fontWeight: 700,
                      lineHeight: 1.25,
                      letterSpacing: "0.02em",
                    }}
                  >
                    {row.selectedCabinetQty ?? row.total_qty} шт.
                  </Typography>
                </Box>
              </Box>
            </Box>
          ))}
      </Box>
    </Box>
  );
}
